import { get } from "lodash";
import { invariant } from "@searchui-searchforms/shared/utils";
import { useEventInstance } from "@/widgets/classic-form/form";
import { FieldComposer, fieldToConfig } from "@/features/form-fields";
import type { SpecialFieldItem } from "@/entities/form-metadata";
import { Grid } from "@/shared/components/Grid";
import { useFormStore } from "@/shared/forms";
import { useCSSSafeId } from "@/shared/hooks/useCSSSafeId";
import { useTrackSearchFormInteraction } from "@/shared/lib/ube";

interface PlaceProps {
    field: SpecialFieldItem;
}

export const Place: React.FC<PlaceProps> = ({ field }) => {
    const fieldData = field.fields[0];
    invariant(fieldData, "Special place field metadata should be provided");

    const { instancePath, locationExactnessPath, setLocation, clearLocation } = useEventInstance({
        eventName: "Self",
    });

    const track = useTrackSearchFormInteraction();
    const onFocus = () => track({ action: "focus", objectName: "special_place" }, { dedup: true });

    const fieldHtmlId = useCSSSafeId();
    // Read the shared value via subscription instead of mounting a `form.Field`. A second
    // `form.Field` on this path (the real event field uses it too) would, in edit mode,
    // re-seed itself from `defaultValues` the instant the instance is removed — resurrecting
    // a just-removed "Any" event. Writes still go through `useEventInstance` below.
    const value = useFormStore((state) => get(state.values, instancePath));

    const fieldConfig = fieldToConfig(fieldData);

    const handleChange = (next: Record<string, unknown>) => {
        const isClearing = !next.location && !next.gpid;

        if (isClearing) {
            clearLocation();
        } else {
            setLocation(next as { location: string; gpid: string });
        }
    };

    // TODO: for special place label should be shown, add once refactoring of base-field related finalized
    return (
        <Grid.Field spanDesktop={9} spanTablet={8} spanMobile={12}>
            <FieldComposer
                fieldPath={instancePath}
                exactnessFieldPath={locationExactnessPath}
                fieldConfig={fieldConfig}
                fieldHtmlId={fieldHtmlId}
            >
                <FieldComposer.Label />
                <FieldComposer.Inputs.Place
                    id={fieldHtmlId}
                    value={value}
                    placeholder={fieldData.meta.placeHolder?.text}
                    data-testid={`placeInput-${instancePath}`}
                    required={fieldConfig.required}
                    onChange={handleChange}
                    onFocus={onFocus}
                />
                <FieldComposer.PlaceExactness />
            </FieldComposer>
        </Grid.Field>
    );
};
